const API_BASE = '/api/artworks';

const _throwFrom = async (response, fallback) => {
  const data = await response.json().catch(() => ({ detail: fallback, code: 'INTERNAL_ERROR' }));
  const err = new Error(data.detail || fallback);
  err.code = data.code || null;
  throw err;
};

export const searchArtworks = async (authenticatedFetch, query, limit = 20) => {
  const params = new URLSearchParams({ q: query, limit: String(limit) });
  const response = await authenticatedFetch(`${API_BASE}/search?${params}`);
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ detail: 'Failed to search artworks', code: 'INTERNAL_ERROR' }));
    const err = new Error(errorData.detail || 'Failed to search artworks');
    err.code = errorData.code || null;
    throw err;
  }
  return response.json();
};

export const createArtwork = async (authenticatedFetch, artworkData) => {
  const response = await authenticatedFetch(API_BASE, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(artworkData),
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ detail: 'Failed to create artwork', code: 'INTERNAL_ERROR' }));
    const err = new Error(errorData.detail || 'Failed to create artwork');
    err.code = errorData.code || null;
    throw err;
  }
  return response.json();
};

export const getAuctionHistory = async (authenticatedFetch, artistName, params = {}) => {
  const qs = new URLSearchParams();
  if (params.from) qs.set('from', String(params.from));
  if (params.to) qs.set('to', String(params.to));
  if (params.price_min != null) qs.set('price_min', String(params.price_min));
  if (params.price_max != null) qs.set('price_max', String(params.price_max));
  const slug = encodeURIComponent(artistName);
  const url = `${API_BASE}/${slug}/auction-history${qs.toString() ? '?' + qs.toString() : ''}`;
  const response = await authenticatedFetch(url);
  if (!response.ok) await _throwFrom(response, 'Failed to fetch auction history');
  return response.json();
};

export const getComparables = async (authenticatedFetch, artistName, params = {}) => {
  const qs = new URLSearchParams();
  if (params.title) qs.set('title', params.title);
  if (params.medium) qs.set('medium', params.medium);
  if (params.year_from) qs.set('year_from', String(params.year_from));
  if (params.year_to) qs.set('year_to', String(params.year_to));
  if (params.price_min != null) qs.set('price_min', String(params.price_min));
  if (params.price_max != null) qs.set('price_max', String(params.price_max));
  if (params.auction_house) qs.set('auction_house', params.auction_house);
  if (params.limit != null) qs.set('limit', String(params.limit));
  if (params.offset != null) qs.set('offset', String(params.offset));
  const slug = encodeURIComponent(artistName);
  const url = `${API_BASE}/${slug}/comparables${qs.toString() ? '?' + qs.toString() : ''}`;
  const response = await authenticatedFetch(url);
  if (!response.ok) await _throwFrom(response, 'Failed to fetch comparables');
  return response.json();
};

export const getMarketIndex = async (authenticatedFetch, artistName) => {
  const slug = encodeURIComponent(artistName);
  const response = await authenticatedFetch(`${API_BASE}/${slug}/market-index`);
  if (!response.ok) await _throwFrom(response, 'Failed to fetch market index');
  return response.json();
};

export const getSellThrough = async (authenticatedFetch, artistName) => {
  const slug = encodeURIComponent(artistName);
  const response = await authenticatedFetch(`${API_BASE}/${slug}/sell-through`);
  if (!response.ok) await _throwFrom(response, 'Failed to fetch sell-through data');
  return response.json();
};

export const getLotVolume = async (authenticatedFetch, artistName) => {
  const slug = encodeURIComponent(artistName);
  const response = await authenticatedFetch(`${API_BASE}/${slug}/lot-volume`);
  if (!response.ok) await _throwFrom(response, 'Failed to fetch lot volume data');
  return response.json();
};
